import { Close } from '@mui/icons-material';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

type Props = {
  show: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  title?: string;
};

function Modal({ show, onClose, children, size = 'md', title }: Props) {
  // Blokowanie scrollowania body gdy modal jest otwarty
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  // Obsługa zamykania przez Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && show) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show, onClose]);

  // Mapa szerokości modalnych okien
  const sizeMap = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  if (!show) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.div
            className={`bg-white rounded-lg shadow-xl overflow-hidden ${sizeMap[size]} w-full relative z-10`}
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{
              type: 'spring',
              stiffness: 350,
              damping: 25,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Nagłówek modalu (opcjonalny) */}
            {title && (
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">{title}</h2>
              </div>
            )}

            {/* Przycisk zamykania */}
            <motion.button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
              onClick={onClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Close fontSize="medium" />
            </motion.button>

            {/* Zawartość modalu */}
            <div className={`p-6 ${title ? '' : 'pt-10'}`}>{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.getElementById('root') as HTMLElement,
  );
}

export default Modal;
